'use client'

/**
 * ResultEmailCaptureCard - inline email capture at the calculator peak moment.
 *
 * Growth-design rule 1 (peak-end) + LinkedIn's "ask at the anticipation moment":
 * the instant a tool produces a result is the highest-intent pixel in the tool,
 * yet today the only email ask on tools is the interruptive exit-intent modal.
 * This card sits in-frame right after the result (below ResultSuccessCard) and
 * asks for the email with the tool-specific deliverable named, not a generic
 * "subscribe". It reuses the same /api/email/subscribe contract + 409 handling
 * as the modal, and fires the same analytics.newsletterSignup event with a
 * surface-specific `source` so per-tool conversion is measurable.
 *
 * Auth: mirrors the modal — never shown to a signed-in user, and held until
 * auth resolves so it never flashes for them.
 *
 * Anti-double-ask: on a successful capture it calls `onCaptured`, which the
 * page wires to dismissNewsletter() + closeNewsletterModal() so the exit-intent
 * modal is suppressed for someone who already signed up here. A soft "Not now"
 * only hides the card locally and leaves the modal's exit-intent fallback intact.
 */

import { useId, useState } from 'react'
import Link from 'next/link'
import { Mail, Check, ArrowRight, Loader2, AlertCircle, Save } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { analytics, stashEmailCaptureAttribution, trackEvent } from '@/lib/analytics'
import { useAuth } from '@/contexts/AuthContext'
import { detectEmailTypo, type EmailSuggestion } from '@/lib/email-typo-detection'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'
import { AppDownloadTextLink } from '@/components/ui/app-download-cta'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'

/**
 * Opt-in "account-led" configuration. When passed, the card promotes Google/Apple
 * account creation to the PRIMARY ask above the email field (peak-moment: turn a
 * correct diagnosis into a saved account), and demotes the email capture below an
 * "or …" divider. The email engine, its `source`/context, and the success state
 * are untouched. Omit this prop entirely and the card renders email-only exactly
 * as before — every existing caller keeps today's behavior.
 */
interface AccountLeadConfig {
  /** OAuth post-sign-in redirect target (usually the current result URL). */
  redirectTo: string
  /** Account-forward headline, e.g. "Save this diagnosis to a free account". */
  headline: string
  /** One line naming what the account keeps. */
  subcopy: string
  /** Divider label introducing the demoted email lane. Defaults to "or get it by email". */
  dividerLabel?: string
  /** Google button label. Defaults to "Save with Google". */
  googleLabel?: string
  /** Apple button label. Defaults to "Save with Apple". */
  appleLabel?: string
  /** Runs immediately before the OAuth redirect. The card already stashes its
   *  own `source` for attribution — only pass this to stash a MORE specific
   *  source or to fire extra analytics. */
  onBeforeOAuth?: () => void
}

interface ResultEmailCaptureCardProps {
  /** Surface-specific source, e.g. "tool_result_fertilizer". Sent as the API context and the PostHog source. */
  source: string
  /** What the user gets, lowercase, e.g. "fertilizer schedule". Used in the default headline + the success line. */
  toolDisplayName: string
  /**
   * One line under the headline naming the ongoing value.
   *
   * NOTE: `subcopy` and `headline` are the EMAIL-ONLY layout's copy. When
   * `accountLead` is passed they are not rendered at all — the account-led
   * branch shows `accountLead.headline` / `accountLead.subcopy` instead. Every
   * tool page currently passes `accountLead`, so edits here change nothing a
   * user sees until that changes. Edit the `accountLead` copy (and its
   * `dividerLabel`) for anything user-visible today.
   */
  subcopy: string
  /** Optional headline override. Defaults to "Get your <toolDisplayName> by email". Ignored when `accountLead` is set — see `subcopy`. */
  headline?: string
  /** Optional success-state line override. Defaults to the 12-month care-calendar
   * copy. Pass a surface-honest line when the capture doesn't send a care plan. */
  successSubcopy?: string
  /** Forwarded to the API so the saved contact carries the grass tag. */
  grassType?: string | null
  /** Forwarded to the API so the saved contact carries the ZIP. */
  zipCode?: string | null
  /** Called after a successful capture. Page wires this to suppress the exit-intent modal. */
  onCaptured?: () => void
  /** Called once when the card's impression fires (50% visible to a signed-out,
   * non-dismissed user). Pages wire this to suppress the exit-intent modal for
   * the session, so nobody is asked the same question twice on one screen. */
  onImpression?: () => void
  /** Optional submit-button label override. Defaults to "Email it to me".
   * Name the deliverable when the surface has a more specific one
   * (e.g. "Remind me in 2 weeks"). */
  ctaLabel?: string
  /** Opt-in: promote Google/Apple account creation to the primary ask above the
   * email field. Omit for the default email-only card. */
  accountLead?: AccountLeadConfig
  className?: string
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function ResultEmailCaptureCard({
  source,
  toolDisplayName,
  subcopy,
  headline,
  successSubcopy,
  grassType,
  zipCode,
  onCaptured,
  onImpression,
  ctaLabel,
  accountLead,
  className,
}: ResultEmailCaptureCardProps) {
  const { user, loading } = useAuth()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null)
  // Fire the focus signal once per mount so impression -> focus -> submit is
  // measurable (closes the "where do they drop" blind spot between the card
  // impression and a successful capture).
  const [focusTracked, setFocusTracked] = useState(false)

  const reactId = useId()
  const headingId = `result-capture-heading-${reactId}`
  const emailId = `result-capture-email-${reactId}`

  // Impression: fire only when the card is actually shown to a signed-out,
  // non-dismissed user, with `source` matching the signup source so the funnel
  // joins. Called before the early return (rules of hooks); `enabled` + the
  // ref-never-mounts-when-gated behavior keep it from firing for signed-in users.
  const impressionRef = useImpressionEvent<HTMLElement>(source, undefined, {
    enabled: !loading && !user && !dismissed,
    // Keep the default analytics event byte-for-byte (surfaceShown adds
    // surface_family) and piggyback the page-level impression callback on the
    // same one-shot observer instead of mounting a second one.
    emit: (key, props) => {
      analytics.surfaceShown(key, props)
      onImpression?.()
    },
  })

  // Match the modal's auth behavior: never ask a signed-in user, and don't
  // flash the card before auth resolves.
  if (loading || user || dismissed) return null

  // Default names the specific deliverable (the tool's own result) rather than a
  // generic "monthly tips" — specificity rule from the growth-design playbook.
  // Call sites can still override with `headline` for a fully custom line.
  const resolvedHeadline = headline ?? `Get your ${toolDisplayName} by email`

  const cardClass = cn(
    'overflow-hidden border border-green-200 bg-linear-to-br from-green-50 to-white shadow-sm',
    'animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none',
    // Account-led variant (Claude Design "OAuth Panel" 1a): compact centered
    // glass card. Email-only keeps its original full-width look.
    accountLead && 'mx-auto w-full',
    className,
  )

  // 1a glass treatment via inline style (custom gradient/rgba/radius/width are
  // not in the precompiled Tailwind bundle). Applied only when accountLead is set.
  const cardStyle = accountLead
    ? {
        maxWidth: '640px',
        background:
          'linear-gradient(180deg, rgba(240,253,244,0.6), #ffffff 60%, rgba(255,251,235,0.3))',
        borderColor: 'rgba(187,247,208,0.6)',
        borderRadius: '24px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.04)',
      }
    : undefined

  // Attribution for the OAuth lane. The card's own `source` is stashed for
  // every account-led caller, so the server callback can put a `capture_source`
  // on signup_completed; a caller that stashes in `onBeforeOAuth` writes the
  // same value again (harmless) or a more specific one (wins).
  function handleOAuthBeforeRedirect() {
    stashEmailCaptureAttribution(source)
    accountLead?.onBeforeOAuth?.()
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (status === 'submitting') return
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      setStatus('error')
      return
    }

    setStatus('submitting')
    setError(null)
    try {
      const response = await fetch('/api/email/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, context: source, grassType, zipCode }),
      })
      const data = await response.json().catch(() => null)

      if (response.ok || data?.error === 'already_subscribed') {
        analytics.newsletterSignup(email, source)
        setStatus('success')
        onCaptured?.()
        return
      }

      if (response.status === 429) {
        setError('Too many requests. Please try again in a few minutes.')
      } else {
        setError(data?.error || 'Something went wrong. Please try again.')
      }
      setStatus('error')
    } catch {
      setError('Network error. Please check your connection and try again.')
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <Card className={cardClass} style={cardStyle}>
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-full bg-green-100 p-2">
              <Check className="h-5 w-5 text-green-700" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-gray-900">You&apos;re subscribed!</h2>
              <p className="mt-0.5 text-sm text-gray-600">
                {successSubcopy ?? `Your free 12-month care calendar is on the way, starting with this month. Your ${toolDisplayName} stays right here to revisit anytime.`}
              </p>
            </div>
          </div>
          <Link
            href="/auth/login"
            onClick={() => trackEvent('newsletter_success_cta_clicked', { destination: 'auth_login', source })}
            className="group mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 sm:w-auto"
          >
            Create your free account
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
          {/* Web → app funnel: quiet post-capture app link. The account CTA above
              stays the frame's primary ask; this self-gates to null pre-launch. */}
          <div className="mt-3">
            <AppDownloadTextLink
              placement={`${source}_success`}
              label="Or get reminders on your phone with the free iPhone app"
            />
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card className={cardClass} style={cardStyle}>
      <aside
        ref={impressionRef}
        // Marks the result region. See result-success-card.tsx — the sticky app
        // bar this guarded against is gone, the marker stays as the stable
        // "this is the answer" hook.
        data-tool-result=""
        data-calc-ignore=""
        aria-labelledby={headingId}
        className={accountLead ? "p-8" : "p-5 sm:p-6"}
      >
        {accountLead ? (
          <>
            {/* Account-led: OAuth account creation is the primary ask; the email
                lane is demoted below the divider. */}
            <div className="flex items-start gap-4">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-green-100">
                <Save className="h-6 w-6 text-green-700" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 id={headingId} className="text-gray-900" style={{ fontSize: "1.375rem", lineHeight: 1.3, fontWeight: 700, letterSpacing: "-0.025em" }}>
                  {accountLead.headline}
                </h2>
                <p className="mt-1.5 text-gray-600" style={{ fontSize: "15px", lineHeight: 1.55 }}>{accountLead.subcopy}</p>
              </div>
            </div>
            <div className="mt-6">
              <SocialLoginButtons
                redirectTo={accountLead.redirectTo}
                onBeforeRedirect={handleOAuthBeforeRedirect}
                pill
                appleSolid
                googleLabel={accountLead.googleLabel ?? 'Save with Google'}
                appleLabel={accountLead.appleLabel ?? 'Save with Apple'}
              />
            </div>
            <div className="mt-4 flex items-center gap-3 text-xs font-medium text-gray-400">
              <span className="h-px flex-1 bg-gray-200" />
              {accountLead.dividerLabel ?? 'or get it by email'}
              <span className="h-px flex-1 bg-gray-200" />
            </div>
          </>
        ) : (
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-full bg-green-100 p-2">
              <Mail className="h-5 w-5 text-green-700" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id={headingId} className="font-semibold text-gray-900">
                {resolvedHeadline}
              </h2>
              <p className="mt-0.5 text-sm text-gray-600">{subcopy}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={accountLead ? 'mt-3 flex flex-col gap-2 sm:flex-row' : 'mt-4 flex flex-col gap-2 sm:flex-row'}>
          <label htmlFor={emailId} className="sr-only">
            Email address
          </label>
          <div className="relative min-w-0 flex-1">
            <Mail
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              id={emailId}
              type="email"
              name="email"
              required
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                const value = e.target.value
                setEmail(value)
                setSuggestion(value.includes('@') && value.includes('.') ? detectEmailTypo(value) : null)
                if (status === 'error') {
                  setStatus('idle')
                  setError(null)
                }
              }}
              onFocus={() => {
                if (focusTracked) return
                setFocusTracked(true)
                trackEvent('result_email_capture_focused', { source })
              }}
              disabled={status === 'submitting'}
              aria-invalid={status === 'error'}
              className="h-12 w-full rounded-xl border border-gray-300 bg-white pl-11 pr-4 text-base shadow-sm transition placeholder:text-gray-400 focus:border-green-600 focus:outline-hidden focus:ring-2 focus:ring-green-500/50 disabled:opacity-60 aria-[invalid=true]:border-red-400 aria-[invalid=true]:ring-red-400/40 sm:h-11 sm:text-sm"
            />
          </div>
          {/* Never render the primary action dimmed while the field is empty —
              a washed-out button reads as broken and suppresses the ask.
              Empty/invalid submits are caught by handleSubmit's inline error. */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="inline-flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-green-700 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60 sm:h-11"
          >
            {status === 'submitting' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <>
                {ctaLabel ?? 'Email it to me'}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>
        </form>

        {suggestion && (
          <button
            type="button"
            onClick={() => {
              setEmail(suggestion.suggested)
              setSuggestion(null)
            }}
            className="mt-2 text-left text-sm text-green-700 hover:underline"
          >
            Did you mean <span className="font-semibold">{suggestion.suggested}</span>?
          </button>
        )}

        {status === 'error' && error && (
          <p role="alert" className="mt-2 flex items-center gap-1.5 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}

        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">Once a month. No spam. Unsubscribe anytime.</p>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="shrink-0 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
          >
            Not now
          </button>
        </div>
      </aside>
    </Card>
  )
}

export default ResultEmailCaptureCard
